import { useForm } from '@tanstack/react-form'
import { FormField } from '@workspace/seedforge-ui/components/form-field'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { Separator } from '@workspace/ui/components/separator'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { AnalyticsExclusionRow } from './types.js'

export interface ExclusionCardProps {
  title: string
  icon: React.ReactNode
  addLabel: string
  dialogTitle: string
  dialogDescription: string
  fieldLabel: string
  inputPlaceholder: string
  validate: (value: string) => string | null
  rows: AnalyticsExclusionRow[]
  onAdd: (value: string) => Promise<unknown>
  onRemove: (id: string) => void
  isRemoving: boolean
}

/**
 * Card + add dialog for a single type of analytics exclusion (email or
 * domain). Uses TanStack Form + shared FormField so field validation and
 * error styling match the profile edit forms.
 */
export function ExclusionCard({
  title,
  icon,
  addLabel,
  dialogTitle,
  dialogDescription,
  fieldLabel,
  inputPlaceholder,
  validate,
  rows,
  onAdd,
  onRemove,
  isRemoving,
}: ExclusionCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { value: '' },
    onSubmit: async ({ value: formValues }) => {
      setSubmitError(null)
      try {
        await onAdd(formValues.value)
        form.reset()
        setDialogOpen(false)
      } catch (e) {
        setSubmitError((e as Error).message || 'Failed to add')
      }
    },
  })

  return (
    <section>
      <div className="overflow-hidden rounded-xl border border-border bg-card/30">
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            {icon}
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            <span className="text-xs text-muted-foreground">{rows.length}</span>
          </div>
          <SecondaryButton
            size="sm"
            icon={<Plus />}
            onClick={() => {
              form.reset()
              setSubmitError(null)
              setDialogOpen(true)
            }}
          >
            {addLabel}
          </SecondaryButton>
        </div>

        {rows.length > 0 && (
          <>
            <Separator />
            <div className="divide-y divide-border">
              {rows.map((row) => (
                <div key={row.id} className="flex items-center gap-4 px-5 py-3">
                  <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                    {icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-foreground">{row.value}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {row.createdBy?.name ? `Added by ${row.createdBy.name}` : 'System default'}
                      {' · '}
                      {new Date(row.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(row.id)}
                    disabled={isRemoving}
                    className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card hover:text-foreground disabled:opacity-50"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              form.handleSubmit()
            }}
          >
            <div className="space-y-4 py-2">
              <form.Field name="value" validators={{ onBlur: ({ value }) => validate(value) }}>
                {(field) => (
                  <FormField
                    label={fieldLabel}
                    error={field.state.meta.errors[0]?.toString() ?? submitError ?? undefined}
                  >
                    <Input
                      autoFocus
                      placeholder={inputPlaceholder}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              </form.Field>
            </div>
            <DialogFooter className="mt-2">
              <SecondaryButton type="button" onClick={() => setDialogOpen(false)}>
                Cancel
              </SecondaryButton>
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
                {([canSubmit, isSubmitting]) => (
                  <CtaButton type="submit" disabled={!canSubmit}>
                    {isSubmitting ? 'Adding...' : 'Add'}
                  </CtaButton>
                )}
              </form.Subscribe>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
